import { createBrowserRouter } from 'react-router';
import { EPath } from './path';
import type { IRouteHandle } from './handle';
import LoadingScreen from '@/components/loading-screen';
import { ErrorBoundary } from './components/error-boundary';
import { FLAGS } from '@/flags';
import { RootLayout } from './components/root-layout';

export const routers = createBrowserRouter([
  {
    HydrateFallback: LoadingScreen,
    ErrorBoundary: ErrorBoundary,
    Component: RootLayout,
    children: [
      {
        path: EPath.Root,
        lazy: async () => {
          const { default: loader } = await import('./loader/auth');
          return { loader };
        }
      },
      {
        path: EPath.Login,
        lazy: async () => {
          const [Component, loader] = await Promise.all([
            import('@/pages/auth/Login').then((m) => m.default),
            import('./loader/login').then((m) => m.default)
          ]);
          return { Component, loader };
        }
      },
      {
        path: EPath.Logout,
        lazy: async () => {
          const { default: loader } = await import('./loader/logout');
          return { loader };
        }
      },
      {
        lazy: async () => {
          const [Component, loader] = await Promise.all([
            import('./components/protect-layout').then((m) => m.ProtectLayout),
            import('./loader/protect').then((m) => m.default)
          ]);
          return { Component, loader };
        },
        children: [
          {
            path: EPath.Portal,
            lazy: async () => {
              const [Component, loader] = await Promise.all([
                import('@/pages/portal').then((m) => m.default),
                import('./loader/enterpriseAdmin').then((m) => m.default)
              ]);
              return { Component, loader };
            }
          },
          {
            path: EPath.Admin,
            // 后台各页签共用同一个 header，切页签只替换 Outlet 内容，不弹全局 loading
            handle: { quietNavigation: true } satisfies IRouteHandle,
            lazy: async () => {
              const [Component, loader] = await Promise.all([
                import('@/pages/admin').then((m) => m.default),
                import('./loader/enterpriseAdmin').then((m) => m.default)
              ]);
              return { Component, loader };
            },
            children: [
              {
                index: true,
                lazy: async () => {
                  const { adminIndexLoader } = await import('./loader/enterpriseAdmin');
                  return { loader: adminIndexLoader };
                }
              },
              {
                path: EPath.AdminTeam,
                lazy: async () => {
                  const [Component, loader] = await Promise.all([
                    import('@/pages/admin/team').then((m) => m.default),
                    import('./loader/adminTeam').then((m) => m.default)
                  ]);
                  return { Component, loader };
                }
              },
              ...(FLAGS.FEATURES.ENABLE_ADMIN_UNIT_PRICE
                ? [
                    {
                      path: EPath.AdminUnitPrice,
                      lazy: async () => {
                        const { default: Component } = await import('@/pages/admin/unit-price');
                        return { Component };
                      }
                    }
                  ]
                : [])
            ]
          },
          {
            path: EPath.Editor,
            // 登出不把用户直接拽离画布：由 pages/app 换成一个确认框，让未保存的改动有交代
            handle: { ownLogout: true } satisfies IRouteHandle,
            // 这个 loader 会重拉方案与整个构件库并交给编辑器内核，跨标签页登录之类的
            // revalidate() 不应该连带重跑它，否则会把未保存的编辑改回服务端版本。
            // 只在 ?scheme 真的换了时才重取——那种情况下继续用旧方案的数据才是 bug
            shouldRevalidate: ({ currentUrl, nextUrl }) => currentUrl.search !== nextUrl.search,
            lazy: async () => {
              const [Component, loader] = await Promise.all([
                import('@/pages/app').then((m) => m.default),
                import('./loader/editor').then((m) => m.default)
              ]);
              return { Component, loader };
            }
          },
          {
            path: EPath.Workbench,
            lazy: async () => {
              if (FLAGS.FEATURES.ENABLE_SCHEME_MANAGE_V1) {
                const { default: Component } = await import('@/pages/feature-workbench/Workbench');
                return { Component };
              } else {
                const { default: Component } = await import('@/pages/workbench/Workbench');
                return { Component };
              }
            }
          },
          {
            path: EPath.Account,
            lazy: async () => {
              const [Component, loader] = await Promise.all([
                import('@/pages/account').then((m) => m.default),
                import('./loader/account').then((m) => m.default)
              ]);
              return { Component, loader };
            }
          },
          {
            path: EPath.Summary,
            // 同 EPath.Editor：概算页的 loader 也会重拉方案与构件库
            shouldRevalidate: ({ currentUrl, nextUrl }) => currentUrl.search !== nextUrl.search,
            lazy: async () => {
              const [Component, loader] = await Promise.all([
                import('@/pages/summary/summary').then((m) => m.default),
                import('./loader/summary').then((m) => m.default)
              ]);
              return { Component, loader };
            }
          },
          ...(FLAGS.FEATURES_DEV.ENABLE_LAYOUT_RESULT_LIST
            ? [
                {
                  path: EPath.Development,
                  children: [
                    {
                      path: 'layout-task',
                      lazy: async () => {
                        const { default: Component } = await import('@/pages/development/layout-task');
                        return { Component };
                      }
                    }
                  ]
                }
              ]
            : [])
        ]
      }
    ]
  }
]);
